import { useMemo, useState } from "react";
import { Inbox, Search, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FeedbackCard } from "./FeedbackCard";

const FILTERS = [
  { key: "all", label: "All", testId: "dashboard-filter-all", test: () => true },
  { key: "attention", label: "Needs attention", testId: "dashboard-filter-needs-attention", test: (i) => i.overall < 3 && !i.addressed },
  { key: "praise", label: "High praise", testId: "dashboard-filter-praise", test: (i) => i.overall >= 4 },
  { key: "open", label: "Unaddressed", testId: "dashboard-filter-unaddressed", test: (i) => !i.addressed },
];

const matches = (i, q) =>
  [i.comment, i.customer_name, i.customer_email, i.business_name].some((v) => v && v.toLowerCase().includes(q));

const SORTS = {
  lowest: (a, b) => a.overall - b.overall || b.created_at.localeCompare(a.created_at),
  highest: (a, b) => b.overall - a.overall || b.created_at.localeCompare(a.created_at),
  newest: (a, b) => b.created_at.localeCompare(a.created_at),
};

export const FeedbackFeed = ({ items, showBusiness, businesses, onToggleAddressed, onContacted, testId = "feedback-feed" }) => {
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("lowest");
  const [biz, setBiz] = useState("all");
  const [query, setQuery] = useState("");
  const term = query.trim();

  const scoped = useMemo(() => {
    const q = term.toLowerCase();
    return items.filter((i) => (biz === "all" || i.business_id === biz) && (!q || matches(i, q)));
  }, [items, biz, term]);
  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    return scoped.filter(f.test).sort(SORTS[sort]);
  }, [scoped, filter, sort]);

  return (
    <section data-testid={testId} className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#778580]" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reviews by keyword, name or email…"
          className="input-base !pl-10 !pr-10" data-testid="feed-search-input" />
        {query && (
          <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-[#778580] hover:bg-slate-100" data-testid="feed-search-clear" aria-label="Clear search">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
        <div className="flex gap-1 p-1 rounded-full bg-slate-100 w-full xl:w-auto overflow-x-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={f.testId}
              onClick={() => setFilter(f.key)}
              className={`flex-1 sm:flex-none whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                filter === f.key ? (f.key === "attention" ? "bg-red-600 text-white" : "bg-white text-[#1A2B25] shadow-sm") : "text-[#4B5A54] hover:text-[#1A2B25]"
              }`}
            >
              {f.label} <span className="opacity-70">{scoped.filter(f.test).length}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {businesses && (
            <Select value={biz} onValueChange={setBiz}>
              <SelectTrigger data-testid="feed-business-filter" className="w-full sm:w-48 rounded-xl bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="dashboard-sort-select" className="w-full sm:w-44 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lowest" data-testid="sort-lowest">Lowest score first</SelectItem>
              <SelectItem value="highest" data-testid="sort-highest">Highest score first</SelectItem>
              <SelectItem value="newest" data-testid="sort-newest">Newest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center" data-testid="feedback-empty-state">
          <Inbox className="w-8 h-8 mx-auto text-[#778580]" />
          <p className="mt-3 font-semibold text-[#1A2B25]">{term ? `No reviews match “${term}”` : "No feedback here yet"}</p>
          <p className="text-sm text-[#778580]">{term ? "Try a different keyword or clear the search." : "Share your survey link or QR code to start collecting responses."}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="feedback-list">
          {shown.map((i) => <FeedbackCard key={i.id} item={i} showBusiness={showBusiness} onToggleAddressed={onToggleAddressed} onContacted={onContacted} term={term} />)}
        </div>
      )}
    </section>
  );
};
